import {useEffect, useRef} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import {
  getGenericContentLink,
  resolveGenericContentCta,
  type GenericContent,
} from '../../../../../../brand/lib/types/contentful'
import {animateParallax, type AnimationParallaxElement} from '../../_utils/parallax'

import {Heading, Prose} from '@primer/react-brand/esm'
import {MediaAsset} from '../../_shared/MediaAsset/MediaAsset'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Eyebrow} from '../../_shared/Eyebrow/Eyebrow'
import {Link} from '../../_shared/Link/Link'
import {Product} from '../../_shared/Product/Product'
import {Spacer} from '../../_shared/Spacer/Spacer'

import Styles from './Highlight.module.css'

type Props = {
  content: GenericContent
  location: string
  sectionIndex: number
}

export function Highlight(props: Props) {
  const {content, location, sectionIndex} = props
  const {heading, subheading, label, text, media} = content.fields
  const productPlacement = media?.at(0)?.fields.id

  const title = heading
  const subTitle = subheading ? documentToPlainTextString(subheading) : ''

  const eyebrow = label?.fields.text
  const description = text ? documentToHtmlString(text) : ''

  const link = getGenericContentLink(content)
  const ctaGroup = resolveGenericContentCta(content, {legacyFallback: 'first'})

  // Animation
  const wrapper2Ref = useRef<HTMLDivElement>(null)

  const assetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements: AnimationParallaxElement[] = []
    if (assetRef.current) elements.push({element: assetRef.current})
    return wrapper2Ref.current ? animateParallax(elements, wrapper2Ref.current) : undefined
  }, [])

  return (
    <div className={Styles.Highlight}>
      {title && (
        <div>
          <Spacer size="64px" size1280="128px" />
          <Heading className={Styles.Title} data-heading="1" as={sectionIndex === 0 ? 'h2' : 'h3'}>
            {title}
          </Heading>
          <Spacer size="32px" size1280="64px" />
        </div>
      )}

      <div className={Styles.River}>
        <div className={Styles.Cell} ref={wrapper2Ref}>
          <div className={Styles.EyebrowWrapper}>
            {subTitle && <Eyebrow className={Styles.SubTitle} text={subTitle} hideIcon />}
            {eyebrow && <Eyebrow className={Styles.EyebrowMobile} text={eyebrow} aria-hidden="true" />}
          </div>

          <Spacer size="24px" size1280="32px" />

          {media && (
            <div className={Styles.MediaWrapper}>
              <Product placement={productPlacement}>
                <div ref={assetRef} data-animate="container">
                  <div data-animate="item">
                    <MediaAsset content={media} location={location} />
                  </div>
                </div>
              </Product>
            </div>
          )}
        </div>

        <div className={Styles.Cell}>
          <div className={Styles.Content}>
            <div>
              {eyebrow && <Eyebrow className={Styles.EyebrowDesktop} text={eyebrow} />}

              {description && (
                <>
                  <Spacer size="24px" size1280="32px" />
                  <div>
                    <Prose className={Styles.Description} html={description} />
                  </div>
                </>
              )}

              {link && (
                <>
                  <Spacer size="24px" size1280="32px" />
                  <div>
                    <Link href={link.fields.href} text={link.fields.text} location={location} />
                  </div>
                </>
              )}
            </div>

            {ctaGroup && (
              <>
                <Spacer size="64px" />
                <div>
                  <CtaButtons contentfulContent={ctaGroup} location={location} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
